import {
  faPen,
  faPlus,
  faTrashAlt,
  type IconDefinition
} from '@fortawesome/pro-light-svg-icons';
import { faEllipsisV } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Button from '@mui/material/Button';
import { useAtom } from 'jotai';
import type React from 'react';
import { useRef } from 'react';

import ContextMenu, { type ContextMenuRefType } from '@/components/ContextMenu';
import PERMISSIONS from '@/constants/auth.constants';
import { PERMISSIONS_LOGIC } from '@/constants/common';
import { useDeviceDialog } from '@/features/DeviceCreate/DeviceCreate.hooks';
import { DEVICE_TYPES_CONFIG, useStyles } from '@/features/DevicesTable';
import { useLogSourcesDialog } from '@/features/LogSourcesCreate/LogSourcesCreate.hooks';
import { selectedOrganizationAtom } from '@/pages/Organizations';
import checkPermissions from '@/services/permissions';
import roleAtom from '@/store/role';
import userAtom from '@/store/user.store';

import { useCrudGuard } from '../../Administration.hooks';
import { DEVICE_STATUS } from '../Devices.constants';
import { selectedDeviceDetailsAtom } from '../Devices.store';

const DeviceDetailContextMenu = () => {
  const { classes } = useStyles();
  const childRef = useRef<ContextMenuRefType>(null);
  // subscribe so role changes re-render and re-evaluate access
  useAtom(roleAtom);
  const [user] = useAtom(userAtom);
  const [selectedOrganization] = useAtom(selectedOrganizationAtom);
  const [selectedDevice] = useAtom(selectedDeviceDetailsAtom);
  const { openDeviceDialog } = useDeviceDialog();
  const { openLogSourcesDialog } = useLogSourcesDialog();

  const menuList: {
    label: string;
    icon: IconDefinition;
    permission: string | string[];
    logic?: PERMISSIONS_LOGIC;
  }[] = [];

  const deviceTypesConfig = DEVICE_TYPES_CONFIG.find(
    (item) => item.id === selectedDevice.device_type_id
  );

  const excludedStatuses = [
    DEVICE_STATUS.DECOMMISSIONING,
    DEVICE_STATUS.CONFIG_SUCCESS,
    DEVICE_STATUS.CONFIG_FAILED,
    DEVICE_STATUS.DEPLOYMENT_FAILED,
    DEVICE_STATUS.JOB_FAILED
  ];

  // Disable Edit and Delete for Lima devices and deleted devices
  if (
    !excludedStatuses.includes(selectedDevice?.status as DEVICE_STATUS) &&
    selectedDevice.device_type_id !== 1
  ) {
    menuList.push({
      label: 'Edit',
      icon: faPen,
      permission: [PERMISSIONS.DEVICES_UPDATE, PERMISSIONS.ADMIN_UPDATE],
      logic: PERMISSIONS_LOGIC.OR
    });
    menuList.push({
      label: 'Delete',
      icon: faTrashAlt,
      permission: [PERMISSIONS.DEVICES_DELETE, PERMISSIONS.ADMIN_UPDATE],
      logic: PERMISSIONS_LOGIC.OR
    });
  }

  // Showing Add/Edit Log sources based on the device type
  if (deviceTypesConfig?.hasSource && selectedDevice?.device_type_id === 2) {
    menuList.push({
      label: `${(selectedDevice?.sources?.length ?? 0) > 0 ? 'Edit' : 'Add'} Log Sources`,
      icon: faPlus,
      permission: PERMISSIONS.DEVICES_CREATE
    });
  }

  const editDeviceHandler = useCrudGuard(() => {
    openDeviceDialog('Edit', selectedDevice?.device_uid);
  });
  const deleteDeviceHandler = useCrudGuard(() => {
    openDeviceDialog('Delete', selectedDevice?.device_uid);
  });
  const addLogSourcesHandler = useCrudGuard(() => {
    openLogSourcesDialog('AddLogSources', selectedDevice?.device_uid);
  });
  const editLogSourcesHandler = useCrudGuard(() => {
    openLogSourcesDialog('EditLogSources', selectedDevice?.device_uid);
  });
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    if (childRef.current) {
      childRef.current.showContextMenu(event.currentTarget, menuList);
    }
  };

  const onClickHandler = (selectedMenu: string) => {
    if (selectedMenu === 'Edit') {
      editDeviceHandler();
    } else if (selectedMenu === 'Delete') {
      deleteDeviceHandler();
    } else if (selectedMenu === 'Add Log Sources') {
      addLogSourcesHandler();
    } else if (selectedMenu === 'Edit Log Sources') {
      editLogSourcesHandler();
    }
  };

  const accessCount = menuList.filter((item) =>
    checkPermissions(item.permission, user, selectedOrganization)
  ).length;

  return accessCount > 0 ? (
    <>
      <Button
        className={classes.ellipsisIcoBtn}
        variant="outlined"
        size="medium"
        id="basic-button"
        onClick={handleClick}
      >
        <FontAwesomeIcon
          icon={faEllipsisV}
          size="sm"
          style={{ margin: '1px' }}
        />
      </Button>

      <ContextMenu
        ref={childRef}
        menuList={menuList}
        className={classes.ellipsisMenu}
        clickHandler={onClickHandler}
      />
    </>
  ) : null;
};

export default DeviceDetailContextMenu;
